import { computeHash } from '../utils'

export type Color = { fg: string; bg: string }

const USER_RED = '#FA3C69'
const USER_RED_BG = '#FDEEF1'
const USER_ORANGE = '#e57c45'
const USER_ORANGE_BG = '#FFF1E9'
const USER_PURPLE = '#412FB6'
const USER_PURPLE_BG = '#EFECFF'
const USER_GREEN = '#60AB6B'
const USER_GREEN_BG = '#ECF7EE'
const USER_BLUE = '#3E9DE9'
const USER_BLUE_BG = '#E9F5FF'
const USER_YELLOW = '#C8A022'
const USER_YELLOW_BG = '#FDF9EC'

const userColors: Color[] = [
    { fg: USER_RED, bg: USER_RED_BG },
    { fg: USER_ORANGE, bg: USER_ORANGE_BG },
    { fg: USER_PURPLE, bg: USER_PURPLE_BG },
    { fg: USER_GREEN, bg: USER_GREEN_BG },
    { fg: USER_BLUE, bg: USER_BLUE_BG },
    { fg: USER_YELLOW, bg: USER_YELLOW_BG },
]
export const computeUserColor = (id: string): Color => computeColor(id, userColors)

const computeColor = (id: string, colors: Color[]): Color =>
    colors[Math.abs(computeHash(id)) % colors.length]

const DASHBOARD_RED = '#FA3C69'
const DASHBOARD_RED_BG = '#FDEEF1'
const DASHBOARD_YELLOW = '#C8A022'
const DASHBOARD_YELLOW_BG = '#FDF9EC'
const DASHBOARD_BLUE = '#3E9DE9'
const DASHBOARD_BLUE_BG = '#EAF6FF'
const DASHBOARD_GREEN = '#62CE9A'
const DASHBOARD_GREEN_BG = '#EFFAF5'
const DASHBOARD_GREY = '#999999'
const DASHBOARD_GREY_BG = '#F4F4F4'

type DashboardTagColorKey = 'red' | 'yellow' | 'blue' | 'green' | 'grey'
export const dashboardTagColors: { [K in DashboardTagColorKey]: Color } = {
    red: { fg: DASHBOARD_RED, bg: DASHBOARD_RED_BG },
    yellow: { fg: DASHBOARD_YELLOW, bg: DASHBOARD_YELLOW_BG },
    blue: { fg: DASHBOARD_BLUE, bg: DASHBOARD_BLUE_BG },
    green: { fg: DASHBOARD_GREEN, bg: DASHBOARD_GREEN_BG },
    grey: { fg: DASHBOARD_GREY, bg: DASHBOARD_GREY_BG },
}

export const intToColorString = (colorInt: number) => '#' + colorInt.toString(16).padStart(6, '0')

const EMPLOYEE_TOMATO = '#FF8585'
const EMPLOYEE_TOMATO_BG = '#FFEBEB'
const EMPLOYEE_TANGERINE = '#FEAD7F'
const EMPLOYEE_TANGERINE_BG = '#FFF2EB'
const EMPLOYEE_SAGE = '#9BE4A6'
const EMPLOYEE_SAGE_BG = '#EFFBF1'
const EMPLOYEE_SEAFOAM = '#92E8E3'
const EMPLOYEE_SEAFOAM_BG = '#EEFBFB'
const EMPLOYEE_TURQUOISE = '#82BCEE'
const EMPLOYEE_TURQUOISE_BG = '#EDF5FD'
const EMPLOYEE_VIOLET = '#B299FF'
const EMPLOYEE_VIOLET_BG = '#F4F0FF'
const EMPLOYEE_ROSE = '#FAA2D7'
const EMPLOYEE_ROSE_BG = '#FEECF7'

type EmployeeColorKey =
    | 'tomato'
    | 'tangerine'
    | 'sage'
    | 'seafoam'
    | 'turquoise'
    | 'violet'
    | 'rose'
export const employeeColors: { [K in EmployeeColorKey]: Color } = {
    tomato: { fg: EMPLOYEE_TOMATO, bg: EMPLOYEE_TOMATO_BG },
    tangerine: { fg: EMPLOYEE_TANGERINE, bg: EMPLOYEE_TANGERINE_BG },
    sage: { fg: EMPLOYEE_SAGE, bg: EMPLOYEE_SAGE_BG },
    seafoam: { fg: EMPLOYEE_SEAFOAM, bg: EMPLOYEE_SEAFOAM_BG },
    turquoise: { fg: EMPLOYEE_TURQUOISE, bg: EMPLOYEE_TURQUOISE_BG },
    violet: { fg: EMPLOYEE_VIOLET, bg: EMPLOYEE_VIOLET_BG },
    rose: { fg: EMPLOYEE_ROSE, bg: EMPLOYEE_ROSE_BG },
}

export const lookupEmployeeColor = (employeeColorKey = '') =>
    employeeColorKey in employeeColors ? employeeColors[employeeColorKey as EmployeeColorKey] : null

const ITEM_ORANGE = '#FFCC80'
const ITEM_ORANGE_BG = '#FFF5E6'
const ITEM_YELLOW = '#F7DE5F'
const ITEM_YELLOW_BG = '#FFFADE'
const ITEM_LIME = '#DCE775'
const ITEM_LIME_BG = '#F8FAE3'
const ITEM_CYAN = '#6FD9E6'
const ITEM_CYAN_BG = '#D4F4F8'
const ITEM_BLUE = '#81D4FA'
const ITEM_BLUE_BG = '#E6F6FE'
const ITEM_PURPLE = '#CE93D8'
const ITEM_PURPLE_BG = '#F5E9F7'
const ITEM_BROWN = '#A1887F'
const ITEM_BROWN_BG = '#ECE7E5'
const ITEM_TEAL = '#3C94A4'
const ITEM_TEAL_BG = '#E2EFF1'
const ITEM_LAVENDER = '#969DD5'
const ITEM_LAVENDER_BG = '#F0F1F9'
const ITEM_GREY = '#CECECE'
const ITEM_GREY_BG = '#F5F5F5'

type BookableItemColorKey = 'orange' | 'yellow' | 'lime' | 'cyan' | 'blue' | 'purple' | 'brown'
export const bookableItemColors: { [K in BookableItemColorKey]: Color } = {
    orange: { fg: ITEM_ORANGE, bg: ITEM_ORANGE_BG },
    yellow: { fg: ITEM_YELLOW, bg: ITEM_YELLOW_BG },
    lime: { fg: ITEM_LIME, bg: ITEM_LIME_BG },
    cyan: { fg: ITEM_CYAN, bg: ITEM_CYAN_BG },
    blue: { fg: ITEM_BLUE, bg: ITEM_BLUE_BG },
    purple: { fg: ITEM_PURPLE, bg: ITEM_PURPLE_BG },
    brown: { fg: ITEM_BROWN, bg: ITEM_BROWN_BG },
}

export type BookableColorKey = BookableItemColorKey | 'teal' | 'lavender' | 'grey'
const bookableColors: { [K in BookableColorKey]: Color } = {
    ...bookableItemColors,
    teal: { fg: ITEM_TEAL, bg: ITEM_TEAL_BG },
    lavender: { fg: ITEM_LAVENDER, bg: ITEM_LAVENDER_BG },
    grey: { fg: ITEM_GREY, bg: ITEM_GREY_BG },
}

export const lookupBookableColor = (key: string) =>
    key in bookableItemColors ? bookableColors[key as BookableColorKey] : bookableColors['grey']

export type BookableDefaultColorKey = 'service' | 'class' | 'reservation' | 'order'
export const bookableDefaultColors: {
    [K in BookableDefaultColorKey | 'default']: BookableColorKey
} = {
    service: 'blue',
    class: 'purple',
    reservation: 'orange',
    order: 'lavender',
    default: 'grey',
}

export const lookupBookableDefaultColorKey = (key: string) =>
    key in bookableDefaultColors
        ? bookableDefaultColors[key as BookableDefaultColorKey]
        : bookableDefaultColors['default']

export const lookupBookableDefaultColor = (key: string) =>
    key in bookableDefaultColors
        ? lookupBookableColor(bookableDefaultColors[key as BookableDefaultColorKey])
        : bookableColors['grey']

export const getDefaultServiceColorKey = () => lookupBookableDefaultColorKey('service')
export const getDefaultClassColorKey = () => lookupBookableDefaultColorKey('class')
export const getDefaultReservationColorKey = () => lookupBookableDefaultColorKey('reservation')
export const getDefaultTaskColorKey = () => lookupBookableDefaultColorKey('task')
export const getDefaultEventColorKey = () => lookupBookableDefaultColorKey('event')
export const getDefaultBlockColorKey = () => lookupBookableDefaultColorKey('block')
export const getDefaultOrderColorKey = () => lookupBookableDefaultColorKey('order')

const STATUS_GREEN = '#508F4B'
const STATUS_GREEN_BG = '#20BC521A'
const STATUS_RED = '#E22216'
const STATUS_RED_BG = '#E222161A'
const STATUS_TEAL = '#3C94A4'
const STATUS_TEAL_BG = '#3C94A41A'
const STATUS_ORANGE = '#B85E0F'
const STATUS_ORANGE_BG = '#B85E0F1A'
const STATUS_GREY = '#555555'
const STATUS_GREY_BG = '#F5F5F5'

type BookableStatusColorKey =
    | 'paid'
    | 'on time'
    | 'pending'
    | 'no show'
    | 'late'
    | 'completed'
    | 'past due'
const bookableStatusColors: { [K in BookableStatusColorKey | 'default']: Color } = {
    paid: { fg: STATUS_GREEN, bg: STATUS_GREEN_BG },
    'on time': { fg: STATUS_GREEN, bg: STATUS_GREEN_BG },
    pending: { fg: STATUS_RED, bg: STATUS_RED_BG },
    'no show': { fg: STATUS_RED, bg: STATUS_RED_BG },
    late: { fg: STATUS_RED, bg: STATUS_RED_BG },
    completed: { fg: STATUS_TEAL, bg: STATUS_TEAL_BG },
    'past due': { fg: STATUS_ORANGE, bg: STATUS_ORANGE_BG },
    default: { fg: STATUS_GREY, bg: STATUS_GREY_BG },
}

export const lookupBookableStatusColor = (key: string) =>
    key in bookableStatusColors
        ? bookableStatusColors[key as BookableStatusColorKey]
        : bookableStatusColors['default']
